import {
  Component,
  InjectionToken,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { AnalyticsService } from '@core/services/analytics.service';
import MarkerClusterer from '@google/markerclusterer';
import { select, Store } from '@ngrx/store';
// import { AllLocationRequestGet, ToggleMap } from '@results/store/results.actions';
import {
  selectContextCore,
  selectContextLocale,
  selectDeviceIsMobile,
} from '@store/context/context.selector';
import { IHotelsAppState } from '@store/index';
import { selectQuery, selectRouterEntity } from '@store/router/router.selector';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil, takeWhile } from 'rxjs/operators';

import { Ga4Service } from '@core/services/ga4.service';
import {
  selectAllLocations,
  selectFilters,
  selectLoading,
  selectViewMap,
} from '../../modules/results/store/results.selector';
import { mapStylesSettings } from './hotels-map.configure';
import { AccommodationType } from '@core/enums/accommodation-type.enum';
import { PriceService } from '@core/services/price.service';
import { CurrencyHelper } from '@core/enums';
import { allLocationRequestGet, toggleMap } from '@results/store/results.actions';

export const GOOGLE = new InjectionToken('google');
export const googleFactory = () => google;

@Component({
  selector: 'am-hotels-map',
  styleUrls: ['./hotels-map.component.scss'],
  templateUrl: './hotels-map.component.html',
})
export class HotelsMapComponent implements OnInit, OnDestroy {
  @ViewChild(GoogleMap, { static: false }) public hotelsMap: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) public infoWindow: MapInfoWindow;
  @ViewChildren(MapMarker) public allMarkers: MapMarker[];
  public currentMarker: MapMarker;
  private unSubscribe$ = new Subject<void>();
  public locations$: Observable<any>;
  public loading$: Observable<boolean>;
  public filters$: Observable<any>;
  public query$: Observable<any>;

  public center: google.maps.LatLngLiteral;
  public markers = [];
  public poiMarkers = [];
  public googleMaps = google.maps;
  public markerClusterer: MarkerClusterer;
  public iconMarkerSelected = `/hotels/results/assets/images/map-icons/marker-selected.svg`;
  public iconCluster = `/hotels/results/assets/images/map-icons/cluster-marker.svg`;
  public readonly iconPoi = `/hotels/results/assets/images/map-icons/poi-selected.svg`;
  public selectedPoi: any;
  private defaultZoom = 14;
  private mapStyles: google.maps.MapTypeStyle[] = mapStylesSettings;
  public mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    gestureHandling: 'greedy',
    mapTypeId: this.googleMaps.MapTypeId.ROADMAP,
    styles: this.mapStyles,
    zoom: this.defaultZoom,
  };

  public markerInfo: {
    info: any;
    type: string;
  };
  public priceByValue: string;

  public entity: any;
  public query: any;
  public filters: any;
  public isViewMap: boolean;
  public locale: string;
  public brand: string;

  constructor(
    private store$: Store<IHotelsAppState>,
    private analyticsService: AnalyticsService,
    private priceService: PriceService,
    private ga4Service?: Ga4Service,
  ) {}

  public infoWindowOptions: google.maps.InfoWindowOptions;

  public ngOnInit(): void {
    this.loading$ = this.store$.pipe(select(selectLoading));
    this.locations$ = this.store$.pipe(select(selectAllLocations));
    this.filters$ = this.store$.pipe(select(selectFilters));
    this.query$ = this.store$.pipe(
      select(selectQuery),
      filter((data) => data !== undefined),
    );
    this.initViewMap();
    this.initLocale();
    this.initEntity();
    this.filters$.pipe(takeWhile(() => this.isViewMap)).subscribe((data) => {
      this.filters = data;
    });

    this.changeScroll();
    this.initMapIcons();
    this.initMap();
    this.store$.pipe(select(selectDeviceIsMobile), take(1)).subscribe((isMobile) => {
      this.mapOptions.zoomControl = !isMobile;
    });
  }

  private buildInfoWindowHeader(hotel: any): HTMLElement {
    const container = document.createElement('div');
    container.className = 'info-window-header';

    if (hotel?.thumbnail?.url) {
      container.style.backgroundImage = `url(${hotel.thumbnail.url})`;
    } else {
      container.classList.add(`no-image-${this.brand}`);
    }

    return container;
  }

  public initMap() {
    this.store$
      .pipe(
        select(selectQuery),
        takeWhile(() => this.isViewMap),
      )
      .subscribe((query) => {
        this.query = query;
        this.refreshLocations();
        this.refreshPois();
        this.setPriceByValue(this.query.queryParams);
      });

    this.store$
      .pipe(
        select(selectAllLocations),
        takeWhile(() => this.isViewMap),
      )
      .subscribe((data: any) => {
        this.clearMarkers();
        if (data && data.locations) {
          this.createMarkers([...data.locations]);
          setTimeout(() => {
            this.changeProjection();
          }, 0);
        }
        if (data.pois && data.pois.length) {
          this.addPoiMarker(data.pois[0]);
        }
      });
  }

  public initViewMap() {
    this.store$.pipe(select(selectViewMap), takeUntil(this.unSubscribe$)).subscribe((isViewMap) => {
      this.isViewMap = isViewMap;
    });
  }

  public initLocale() {
    this.store$
      .pipe(
        select(selectContextLocale),
        takeWhile(() => this.isViewMap),
      )
      .subscribe((locale: string) => {
        this.locale = locale;
      });
  }

  public initEntity() {
    this.store$
      .pipe(
        select(selectRouterEntity),
        takeWhile(() => this.isViewMap),
      )
      .subscribe((data) => {
        this.entity = data;
      });
  }

  private refreshLocations() {
    this.store$.dispatch(
      allLocationRequestGet({ payload: { entity: this.entity, params: this.query.queryParams } }),
    );
  }

  private refreshPois() {
    const poiId = this.getNearByParam();
    this.setSelectedPoiInfo(poiId);
  }

  private setSelectedPoiInfo(poiId) {
    const poisInFilters =
      this.filters && this.filters.list
        ? // tslint:disable-next-line:no-shadowed-variable
          this.filters.list.find((filter) => filter.type === 'NEARBY')
        : null;
    this.selectedPoi =
      poisInFilters && poisInFilters.values
        ? poisInFilters.values.find((nearbyFilter) => nearbyFilter.code === poiId)
        : null;
  }

  public setPriceByValue(params) {
    this.priceByValue = params.priceBy || 'NIGHT';
  }

  private clearMarkers() {
    this.markers = [];
    this.allMarkers = null;
    this.currentMarker = null;
    this.poiMarkers = [];
  }

  // THIS SHOULD CHANGE
  public initMapIcons() {
    this.store$
      .pipe(
        select(selectContextCore),
        takeWhile(() => this.isViewMap),
      )
      .subscribe((context) => {
        this.brand = context.brand;
      });
  }

  public closeMap() {
    this.trackCloseMap();
    this.clearMarkers();
    this.store$.dispatch(toggleMap({ payload: { viewMap: false } }));
    this.changeScroll();
  }

  private trackCloseMap() {
    this.analyticsService.trackEvent({
      action: 'View Map Button',
      label: 'Hide',
      section: 'Results',
    });
    if (this.ga4Service) {
      this.ga4Service.user_interaction({
        action: 'click',
        label: 'Cerrar',
        section: 'mapa',
        type: 'mapa',
      });
    }
  }

  private trackOpenInfoWindow() {
    this.analyticsService.trackEvent({
      action: 'Map selected',
      label: 'Show',
      section: 'Results',
    });
  }

  private trackCloseInfoWindow() {
    this.analyticsService.trackEvent({
      action: 'Map selected',
      label: 'Hide',
      section: 'Results',
    });
  }

  private createMarkers(hotels: any[]) {
    if (hotels.length) {
      const firstHotel = hotels.shift();

      this.center = {
        lat: firstHotel.geo_position.latitude,
        lng: firstHotel.geo_position.longitude,
      };

      this.createCluster(firstHotel);

      hotels.forEach((hotel: any) => {
        this.createCluster(hotel);
      });
    } else {
      this.center = {
        lat: 0,
        lng: 0,
      };
    }
  }

  private addPoiMarker(poi) {
    const poiMarker = {
      id: poi.entity_id,
      options: {
        icon: this.iconPoi,
      },
      position: new this.googleMaps.LatLng(
        poi.points[0].coordinates[1],
        poi.points[0].coordinates[0],
      ),
      title: this.selectedPoi ? this.selectedPoi.description : '',
      type: 'poi',
    };
    this.poiMarkers.push(poiMarker);
  }

  private createCluster(hotel: any) {
    const formattedPrice = this.formatCurrency(
      this.priceService.getMapPrice(hotel, this.priceByValue),
      CurrencyHelper.getCurrencySymbol(CurrencyHelper.toEnumMember(hotel.currency.code)),
    );
    const isHomestays =
      hotel.accommodation_type && hotel.accommodation_type.code
        ? hotel.accommodation_type.code === AccommodationType.TemporaryRent
        : false;
    const marker = {
      id: hotel.id,
      info: {
        cost_and_taxes: hotel.cost_and_taxes ? hotel.cost_and_taxes : 0,
        discount_percentage: hotel.discount_percentage ? hotel.discount_percentage : 0,
        id: hotel.id ? hotel.id : '',
        included_taxes: hotel.included_taxes ? hotel.included_taxes : false,
        name: hotel.name ? hotel.name : '',
        payment_methods: hotel.payment_methods ? hotel.payment_methods : [],
        per_night: hotel.per_night ? hotel.per_night : '',
        price: hotel.price ? hotel.price : 0,
        priceByValue: this.priceByValue ? this.priceByValue : '',
        price_without_discount: hotel.price_without_discount ? hotel.price_without_discount : 0,
        service_fee: hotel.service_fee ? hotel.service_fee : 0,
        slug: hotel.slug ? hotel.slug : '',
        stars: hotel.stars ? hotel.stars : 0,
        thumbnail: hotel.thumbnail ? hotel.thumbnail : {},
        total: hotel.total ? hotel.total : 0,
        total_with_operation_cost: hotel.total_with_operation_cost
          ? hotel.total_with_operation_cost
          : 0,
        total_without_discount: hotel.total_without_discount ? hotel.total_without_discount : 0,
        category_code:
          hotel.accommodation_type && hotel.accommodation_type.code
            ? hotel.accommodation_type.code
            : '',
        isHomestays,
        currency: hotel.currency.code,
        amenities: hotel.amenities,
        meal_plan: hotel.meal_plan,
        country: hotel.country,
        rate_id: hotel.rate_id,
      },
      options: {
        icon:
          'data:image/svg+xml;charset=UTF-8;base64,' +
          btoa(this.generateIcon(formattedPrice, '#3e8a8c', !isHomestays)),
        hotelMarker: true,
        hotelPrice: formattedPrice,
        isHomestays,
      },
      position: new this.googleMaps.LatLng(
        hotel.geo_position.latitude,
        hotel.geo_position.longitude,
      ),
      title: hotel.name,
      type: 'location',
    };
    this.markers.push(marker);
  }

  public ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.unsubscribe();
  }

  public openInfo(selectedMarker: MapMarker, content: any) {
    this.trackOpenInfoWindow();
    this.resetCurrentMarker();

    this.markerInfo = {
      info: content.info,
      type: content.type,
    };

    if (content.type === 'location') {
      this.infoWindowOptions = {
        headerContent: this.buildInfoWindowHeader(content.info),
        disableAutoPan: false,
      };
    }

    this.currentMarker = selectedMarker;
    this.currentMarker.marker.setIcon({ url: this.iconMarkerSelected });
    this.openInfoWindow(selectedMarker);
  }

  public openInfoWindow(selectedMarker) {
    if (this.infoWindow) {
      this.infoWindow.open(selectedMarker);
    }
  }

  public closeInfoWindow() {
    if (this.infoWindow) {
      this.infoWindow.close();
    }
  }

  public closeHotelMapInfoWindow() {
    this.trackCloseInfoWindow();
    this.resetCurrentMarker();
  }

  public openPoiInfo(selectedMarker: MapMarker, poi: any) {
    this.trackOpenInfoWindow();
    this.resetCurrentMarker();
    this.markerInfo = {
      info: poi.title,
      type: poi.type,
    };
    this.openInfoWindow(selectedMarker);
  }

  public resetCurrentMarker() {
    if (this.currentMarker) {
      const marker = this.currentMarker.marker;
      marker.setIcon({
        url:
          'data:image/svg+xml;charset=UTF-8;base64,' +
          btoa(this.generateIcon(marker.get('hotelPrice'), '#56C6C8', !marker.get('isHomestays'))),
      });
    }
    this.closeInfoWindow();
    this.currentMarker = null;
  }

  public changeProjection() {
    const wrappedMarker = this.getWrappedMarker();
    if (this.hotelsMap && this.hotelsMap.googleMap) {
      this.markerClusterer = new MarkerClusterer(this.hotelsMap.googleMap, wrappedMarker, {
        maxZoom: 18,
        styles: [
          {
            backgroundPosition: '0 0',
            className: 'cluster-map-icon',
            fontWeight: 'bold',
            height: 32,
            textColor: 'var(' + '--color-white' + ')',
            textLineHeight: 26,
            textSize: 12,
            url: this.iconCluster,
            width: 32,
          },
        ],
      });
    }
  }

  private getWrappedMarker() {
    return this.allMarkers
      ? this.allMarkers.map((m) => m.marker).filter((m) => m && m.get('hotelMarker'))
      : null;
  }

  public getNearByParam() {
    return this.query.queryParams ? this.query.queryParams.NEARBY : '';
  }

  private changeScroll() {
    const body = document.getElementsByTagName('body')[0];

    if (body.style.overflowY === 'hidden') {
      body.style.overflowY = 'scroll';
    } else {
      body.style.overflowY = 'hidden';
    }
  }

  private generateIcon(price: string, backgroundColor: string, showHotelIcon: boolean): string {
    const hotelIcon = `<g transform="translate(6, 3) scale(1.2)">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.69098 3.66324C6.69098 3.46832 6.849 3.3103 7.04392 3.3103H10.948C11.143 3.3103 11.301 3.46832 11.301 3.66324C11.301 3.85817 11.143 4.01619 10.948 4.01619H7.04392C6.849 4.01619 6.69098 3.85817 6.69098 3.66324Z" fill="white"/>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M2.00348 14.9573C2.00348 14.7624 2.1615 14.6044 2.35642 14.6044L15.6436 14.6044C15.8385 14.6044 15.9966 14.7624 15.9966 14.9573C15.9966 15.1522 15.8385 15.3103 15.6436 15.3103L2.35642 15.3103C2.1615 15.3103 2.00348 15.1522 2.00348 14.9573Z" fill="white"/>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M8.24551 5.80281C8.4531 5.65894 8.71648 5.57715 9.03729 5.57715C9.35831 5.57715 9.6225 5.65898 9.83239 5.79983C10.04 5.93912 10.1737 6.12213 10.2591 6.29406C10.3365 6.44983 10.3771 6.602 10.3988 6.71912C10.4463 6.73802 10.5012 6.76023 10.5625 6.78567C10.7897 6.88001 11.1061 7.0192 11.4645 7.19947C12.1762 7.55737 13.0759 8.08808 13.7694 8.76533C14.4776 9.45694 14.9297 10.3475 15.2094 11.163C15.4894 11.9792 15.6054 12.7456 15.6253 13.2148C15.6294 13.3109 15.594 13.4045 15.5275 13.474C15.4609 13.5434 15.3689 13.5827 15.2727 13.5827H2.72782C2.62767 13.5827 2.53224 13.5401 2.4653 13.4657C2.39836 13.3912 2.36622 13.2917 2.37689 13.1922C2.48191 12.2119 2.70944 10.5102 4.13206 8.7927C4.86061 7.91311 5.83047 7.37361 6.60116 7.05506C6.98885 6.89482 7.33232 6.78794 7.57987 6.7209C7.63127 6.70697 7.67861 6.69475 7.72143 6.6841C7.73987 6.57483 7.77326 6.44215 7.83428 6.30639C7.91263 6.13205 8.04013 5.94516 8.24551 5.80281ZM8.11349 7.31949L8.11344 7.3195L8.11014 7.32012L8.09408 7.32323C8.07927 7.32617 8.0564 7.33086 8.02624 7.33751C7.9659 7.3508 7.87655 7.37186 7.76441 7.40223C7.53984 7.46306 7.22558 7.56078 6.8708 7.70742C6.15658 8.00262 5.30265 8.48603 4.67568 9.24297C3.55322 10.5981 3.2507 11.9352 3.125 12.8768H14.8882C14.839 12.4773 14.7328 11.9491 14.5417 11.392C14.2836 10.6394 13.8792 9.85916 13.2762 9.27035C12.6586 8.66716 11.8331 8.17498 11.1474 7.83009C10.8071 7.65896 10.5067 7.52679 10.2918 7.43761C10.1845 7.39306 10.0988 7.35934 10.0405 7.33695C10.0113 7.32575 9.98898 7.3174 9.97424 7.31195L9.95792 7.30595L9.9542 7.3046L9.95352 7.30436L9.95348 7.30434L9.95344 7.30433C9.81389 7.25436 9.72051 7.12256 9.71948 6.9745L9.71942 6.97202C9.7193 6.96853 9.71901 6.96178 9.71834 6.95226C9.71698 6.93309 9.71409 6.90342 9.70804 6.86689C9.69567 6.79216 9.67158 6.69797 9.62693 6.60808C9.58294 6.51952 9.52262 6.44204 9.43905 6.38596C9.35781 6.33145 9.23383 6.28303 9.03729 6.28303C8.84054 6.28303 8.72191 6.33148 8.64761 6.38298C8.5711 6.436 8.51684 6.50959 8.47813 6.59575C8.43869 6.68349 8.41923 6.77623 8.41043 6.85069C8.40613 6.88698 8.40465 6.9165 8.4042 6.93562C8.40398 6.94511 8.40401 6.95184 8.40406 6.95534L8.40409 6.95739L8.40406 6.95655L8.40403 6.95601L8.40402 6.9557C8.40405 6.95641 8.40408 6.95712 8.40411 6.95783C8.41122 7.1338 8.28742 7.28824 8.11387 7.31942C8.11385 7.31943 8.11382 7.31943 8.1138 7.31944C8.11379 7.31944 8.11379 7.31944 8.11379 7.31944C8.11379 7.31944 8.11379 7.31944 8.11378 7.31944C8.11374 7.31945 8.11371 7.31945 8.11367 7.31946C8.11361 7.31947 8.11355 7.31948 8.11349 7.31949ZM9.71947 6.97238C9.71947 6.97243 9.71947 6.97247 9.71947 6.97252ZM10.0724 6.97205C9.71947 6.97205 9.71947 6.97221 9.71947 6.97238C9.71947 6.97227 9.71947 6.97215 9.71947 6.97205H10.0724Z" fill="white"/>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.92757 8.5891C8.0109 8.76531 7.93561 8.97571 7.7594 9.05905C7.38783 9.23477 6.5623 9.69108 6.13567 10.1263C5.70546 10.5652 5.21302 11.4452 5.01708 11.8444C4.9312 12.0194 4.71972 12.0916 4.54474 12.0057C4.36975 11.9198 4.29753 11.7084 4.38342 11.5334C4.58228 11.1282 5.11589 10.1583 5.63157 9.63221C6.15083 9.10245 7.07338 8.60264 7.45762 8.42093C7.63383 8.33759 7.84423 8.41288 7.92757 8.5891Z" fill="white"/>
                              </g>`;
    const temporalRent = `<g transform="translate(3, 2)">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M11.7287 4.02258C11.4799 4.07836 11.2325 4.19904 11.0373 4.35984C10.9705 4.41489 9.2113 6.16529 7.12793 8.24966C4.14913 11.2299 3.33194 12.0574 3.30222 12.1235C3.17996 12.3957 3.3469 12.7224 3.63634 12.7773C3.73501 12.796 3.85978 12.7799 3.94341 12.7375C3.96523 12.7265 5.71534 10.9874 7.83255 8.87283C12.1495 4.5613 11.7427 4.94105 12.0464 4.93962C12.3471 4.93821 11.9424 4.56038 16.2696 8.88213C20.4603 13.0675 20.1634 12.7858 20.3826 12.7843C20.5632 12.783 20.735 12.6677 20.8047 12.5011C20.8418 12.4123 20.8391 12.2161 20.7996 12.1288C20.7571 12.0348 13.2011 4.46663 12.9953 4.31194C12.83 4.18762 12.6662 4.1054 12.4718 4.04907C12.2927 3.9972 11.9038 3.98333 11.7287 4.02258ZM5.7799 12.9905C5.66698 13.0326 5.58194 13.1154 5.51898 13.2447L5.47802 13.3288V16.4869V19.6449L5.52425 19.7354C5.57599 19.8368 5.66634 19.9242 5.7663 19.9696C5.82679 19.9971 6.05193 20 8.15957 20C10.4821 20 10.4862 19.9999 10.5702 19.9609C10.6622 19.918 10.7615 19.8259 10.81 19.7384C10.8384 19.6872 10.8419 19.4775 10.8505 17.3184C10.8605 14.7815 10.8551 14.9158 10.9537 14.7583C11.0205 14.6517 11.1542 14.542 11.2702 14.4986C11.3753 14.4593 11.3994 14.4582 12.0722 14.4634L12.7659 14.4687L12.8686 14.5172C12.9997 14.579 13.1227 14.702 13.1846 14.8331L13.233 14.9359L13.2424 17.2997L13.2517 19.6636L13.2924 19.7397C13.3447 19.8374 13.4606 19.9418 13.5529 19.9744C13.6129 19.9956 14.0278 20 15.9426 20H18.2598L18.3473 19.9569C18.3955 19.9331 18.4645 19.8809 18.5007 19.8408C18.633 19.6944 18.6252 19.9168 18.6197 16.4391C18.6149 13.387 18.6139 13.3083 18.5786 13.2372C18.4718 13.0219 18.2385 12.9186 18.0052 12.9834C17.9152 13.0084 17.7676 13.1407 17.7249 13.2346C17.6922 13.3068 17.6906 13.4369 17.6902 16.1832L17.6898 19.0563H15.9337H14.1776L14.1722 16.9307L14.1668 14.805L14.1246 14.6681C13.9425 14.0778 13.4653 13.6446 12.8863 13.5438C12.8054 13.5297 12.504 13.5248 11.9715 13.5291C11.2001 13.5354 11.1738 13.5368 11.0479 13.5788C10.5091 13.7586 10.1439 14.1264 9.97792 14.6562L9.92547 14.8237L9.92033 16.94L9.91521 19.0563H8.1688H6.42239L6.41738 16.1832L6.41236 13.3101L6.37104 13.2328C6.32139 13.14 6.23354 13.0491 6.15358 13.0077C6.07334 12.9663 5.87018 12.9569 5.7799 12.9905Z" fill="white"/>
                                    </g>`;
    let arrowPosition;
    let width;
    if (price.length <= 7) {
      arrowPosition = `35,28 40,32 45,28`;
      width = `80`;
    } else if (price.length > 7 && price.length <= 9) {
      arrowPosition = `40,28 45,32 50,28`;
      width = `90`;
    } else {
      arrowPosition = `45,28 50,32 55,28`;
      width = `100`;
    }

    return `<svg width="${width}" height="32" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <style type="text/css">
                  @font-face {
                    font-display: swap;
                    font-family: ProximaNova;
                    font-style: normal;
                    font-weight: 400;
                    src: url(https://fonts.almundo.com/ProximaNova/proxima-nova-regular.woff2)
                        format('woff2'),
                      url(https://fonts.almundo.com/ProximaNova/proxima-nova-regular.woff) format('woff');
                  }
                  text {
                    font-family: 'ProximaNova', 'Proxima Nova', 'Roboto', sans-serif;
                  }
                </style>
              </defs>
                <!-- Marker Background -->
              <rect  width="${width}" height="28" rx="8" ry="8" fill="${backgroundColor}"/>
              <polygon  points="${arrowPosition}" fill="${backgroundColor}"/>

              <!-- Marker Text -->
                  <text x="29" y="19" font-family="" font-weight="400" font-size="12" fill="white">
                      ${price}
                  </text>

              <!-- Icon -->
              ${showHotelIcon ? hotelIcon : temporalRent}
            </svg>`;
  }

  private formatCurrency(num: number, currencyLabel = '$'): string {
    // Convert the number to a string and use regex to add periods as thousand separators
    const formattedNumber = num.toLocaleString('en-US').replace(/,/g, '.');

    // Add the dollar sign
    return `${currencyLabel} ${formattedNumber}`;
  }
}
